import {
    ApiShopperLandingPageLink,
    isApiCardLandingPageLink,
    isApiFullLandingPageLink,
} from '@storefronts/common-types'

/** Inputs merged in the same order as fetchAndCacheLinksForHashes (later layers overwrite earlier for the same linkHash). */
export type LandingPageLinkHashMapInputs<
    TLink extends ApiShopperLandingPageLink = ApiShopperLandingPageLink,
> = {
    sessionCachedLinks: Map<string, TLink>
    productHistoryLinks: TLink[]
    staticLandingPageLinks: TLink[]
}

export function normalizeLinkHashes(
    linkHashes?: readonly string[] | null,
): string[] {
    return (linkHashes ?? []).filter((h) => !!h && h.trim().length > 0)
}

function landingPageLinkEnrichmentRank(
    link: ApiShopperLandingPageLink,
): number {
    if (isApiFullLandingPageLink(link)) return 2
    if (isApiCardLandingPageLink(link)) return 1
    return 0
}

/** Later layers win at the same enrichment; a richer link is never replaced by a poorer one. */
export function mergeLandingPageLink<TLink extends ApiShopperLandingPageLink>(
    existing: TLink | undefined,
    incoming: TLink,
): TLink {
    if (!existing) return incoming
    return landingPageLinkEnrichmentRank(incoming) >=
        landingPageLinkEnrichmentRank(existing)
        ? incoming
        : existing
}

export function buildLandingPageLinkHashMap<
    TLink extends ApiShopperLandingPageLink,
>(inputs: LandingPageLinkHashMapInputs<TLink>): Map<string, TLink> {
    const hashToLink = new Map<string, TLink>()
    const setLink = (link: TLink) => {
        hashToLink.set(
            link.linkHash,
            mergeLandingPageLink(hashToLink.get(link.linkHash), link),
        )
    }
    inputs.sessionCachedLinks.forEach(setLink)
    inputs.productHistoryLinks.forEach(setLink)
    inputs.staticLandingPageLinks.forEach(setLink)
    return hashToLink
}

export type OrderedLinksResolution<
    TLink extends ApiShopperLandingPageLink = ApiShopperLandingPageLink,
> = {
    validHashes: string[]
    /** Links we already know for this hash list, in section order (omits hashes not in the map yet). */
    orderedKnownLinks: TLink[]
    missingHashes: string[]
}

/**
 * Partition requested hashes into known ordered links vs hashes that still need a fetch.
 * Used for SSR (sync) and as the first phase of fetchAndCacheLinksForHashes.
 */
export function resolveOrderedLinksAgainstMap<
    TLink extends ApiShopperLandingPageLink,
>(
    linkHashes: readonly string[] | null | undefined,
    hashToLink: Map<string, TLink>,
): OrderedLinksResolution<TLink> {
    const validHashes = normalizeLinkHashes(linkHashes)
    const orderedKnownLinks = validHashes
        .map((hash) => hashToLink.get(hash))
        .filter((link): link is TLink => link != null)
    const missingHashes = validHashes.filter((hash) => !hashToLink.has(hash))
    return { validHashes, orderedKnownLinks, missingHashes }
}
